import { TrendingUp, X } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { useShowAICoPilot } from "../../../../../hooks/useShowAICoPilot";
import { identifyProblematicPatterns } from "../../../../../services/ai/PatternIdentifier";
import {
  getScanHistory,
  getScanSessions
} from "../../../../../services/ai/ScanHistoryTracker";

const DISMISSAL_KEY = "proactivePatternBannerDismissed";
const MIN_SESSIONS = 3;

interface ProactivePatternBannerProps {
  venueId: string;
  onRunSpaceOptimiser: () => void;
}

export const ProactivePatternBanner = ({
  venueId,
  onRunSpaceOptimiser
}: ProactivePatternBannerProps): React.ReactElement | null => {
  const isFeatureEnabled = useShowAICoPilot();
  const [dismissed, setDismissed] = useState(() => {
    try {
      const stored = sessionStorage.getItem(DISMISSAL_KEY);
      if (!stored) return false;
      const parsed = JSON.parse(stored) as { venueId: string };
      return parsed.venueId === venueId;
    } catch {
      return false;
    }
  });

  const patterns = useMemo(() => {
    if (!isFeatureEnabled) return [];

    const sessions = getScanSessions(venueId);
    if (sessions.length < MIN_SESSIONS) return [];

    const decisions = getScanHistory(venueId);
    return identifyProblematicPatterns(sessions, decisions);
  }, [venueId, isFeatureEnabled]);

  const topPattern = patterns[0] ?? null;

  const handleDismiss = useCallback((): void => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISSAL_KEY, JSON.stringify({ venueId }));
    } catch {
      // sessionStorage may be unavailable in some contexts
    }
  }, [venueId]);

  const handleRunOptimiser = useCallback((): void => {
    handleDismiss();
    onRunSpaceOptimiser();
  }, [handleDismiss, onRunSpaceOptimiser]);

  if (!isFeatureEnabled) return null;
  if (dismissed) return null;
  if (!topPattern) return null;

  return (
    <div
      className="mx-3 mt-2 flex items-start gap-3 rounded-lg border border-[var(--borderColor)] bg-[var(--pageBackground)] p-3"
      role="alert"
      aria-label="Pattern insight"
    >
      <TrendingUp
        className="mt-0.5 size-4 flex-shrink-0 text-[var(--primaryButtonSolid)]"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-[var(--primaryTextColor)]">
          {topPattern.description}
        </p>
        <p className="mt-1 text-xs text-[var(--secondaryColor)]">
          {topPattern.suggestion}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            className="rounded-md bg-[var(--primaryButtonSolid)] px-3 py-1 text-xs font-medium text-primary-foreground transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--primaryButtonSolid)] focus:ring-offset-1"
            onClick={handleRunOptimiser}
          >
            Run Space Optimiser
          </button>
          <button
            className="rounded-md border border-[var(--borderColor)] bg-[var(--panelBackground)] px-3 py-1 text-xs font-medium text-[var(--secondaryColor)] transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-[var(--borderColor)] focus:ring-offset-1"
            onClick={handleDismiss}
          >
            Dismiss
          </button>
        </div>
      </div>
      <button
        className="flex-shrink-0 rounded-md p-0.5 text-[var(--secondaryColor)] transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-[var(--borderColor)]"
        onClick={handleDismiss}
        aria-label="Dismiss pattern insight"
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
};
